import type { TicketingSystem } from "@/domain/ticketing"

export type IncludeExclude = "Include" | "Exclude"

export interface FilterResourceCondition {
  exactKey: string
  operator: string
  property: string
  resource: string
  value: string
}

export interface ResourceCondition {
  includeExclude: IncludeExclude
  filters: FilterResourceCondition[]
}

export interface Trigger {
  category: string
  type: string
}
export interface OnDemandField {
  key: string
  value?: string | number
  type: string
}

export interface JiraIssueActionPayload {
  fields: Jira.CreateIssuePayload["jiraIssueFields"]["fields"]
  onDemandFields?: {
    [fieldName: string]: OnDemandField
  }
}

// project/type are {id}; title, description and the vendor's own field keys are plain values
export interface TicketFields {
  [key: string]: unknown
  project: { id: string }
  type: { id: string }
  title: string
  description?: string
}

export interface TicketIssueActionPayload {
  fields: TicketFields
  onDemandFields?: {
    [fieldName: string]: OnDemandField
  }
}

export interface ActionValues {
  webhookIds?: string[]
  labelIds?: string[]
  reason?: string
  undismissOnKaiConfirmed?: boolean
  jiraIssue?: JiraIssueActionPayload
  azureBoardsIssue?: TicketIssueActionPayload
  asanaTask?: TicketIssueActionPayload
  githubIssue?: TicketIssueActionPayload
  slackChannelIds?: string[]
  messageTemplate?: string
}

export interface Action {
  type: ActionType
  values: ActionValues
  relatedError?: Jira.IssueError | ActionError
}

export interface ActionError {
  errorCode: string
  userMessages: string[]
  fields?: Record<string, string>
}

export interface Workflow {
  id: string
  name: string
  description: string
  enabled: boolean
  createdBy: string
  actions: Action[]
  resourceConditions: ResourceCondition[]
  trigger: Trigger
  filterConditions: Record<string, unknown>[]
  lastTriggered: number
  timesTriggered: number
  isError: boolean
}

export type WorkflowResponse = KosmosApi.Response<Workflow[]>

export interface TriggerOptionWorkflow {
  key: string
  disabled: boolean
  displayName: string
  types: { key: string; displayName: string }[]
}

export type TriggerOptionsWorkflowResponse = KosmosApi.Response<
  TriggerOptionWorkflow[]
>

export const ActionTypes = {
  WEBHOOK: "webhook" as const,
  LABEL: "label" as const,
  DISMISS: "dismiss" as const,
  JIRA_ISSUE: "jira_issue" as const,
  REOPEN: "reopen" as const,
  SLACK_MESSAGE: "slack_message" as const,
  AZURE_BOARDS_ISSUE: "azure_boards_issue" as const,
  ASANA_TASK: "asana_task" as const,
  GITHUB_ISSUE: "github_issue" as const
} as const

export type TicketActionType =
  | typeof ActionTypes.AZURE_BOARDS_ISSUE
  | typeof ActionTypes.ASANA_TASK
  | typeof ActionTypes.GITHUB_ISSUE

export type StatusActionType =
  typeof ActionTypes.DISMISS | typeof ActionTypes.REOPEN
export type ActionType = (typeof ActionTypes)[keyof typeof ActionTypes]

interface WebhookExtra {
  type: "webhook"
  webhooks: { id: string; url: string; name: string }[]
}

interface LabelExtra {
  type: "label"
  labels: { id: string; name: string; color: string }[]
}

export interface DynamicTag {
  displayName: string
  key: string
  description: string
  example: string
  valueType: string
}

export interface JiraIssueExtra {
  type: "jira_issue"
  dynamicTags: DynamicTag[]
  defaultIssueTemplate: {
    description: string
    summary: string
  }
}

export interface TicketIssueExtra {
  type: TicketActionType
  dynamicTags: DynamicTag[]
  defaultIssueTemplate: {
    title?: string
    summary?: string
    description?: string
  }
}

export interface SlackMessageExtra {
  type: "slack_message"
  channels: { id: string; name: string }[] | null
  dynamicTags: DynamicTag[] | null
  defaultMessageTemplate: string
}

type ActionExtra =
  | WebhookExtra
  | LabelExtra
  | JiraIssueExtra
  | SlackMessageExtra
  | TicketIssueExtra
  | null

export interface ActionWorkflow {
  key: string
  displayName: string
  disabled: boolean
  // why a disabled action can't be picked, e.g. its integration isn't connected
  disabledReason?: string | null
  extra: ActionExtra
}

export interface ActionOption {
  label: string
  value: string
  color?: string
}

export type ActionWorkflowResponse = KosmosApi.Response<ActionWorkflow>

export type ActionsOptionsWorkflowResponse = KosmosApi.Response<
  ActionWorkflow[]
>

export interface ActionsStatistics {
  webhook: number
  label: number
  dismiss: number
  jira_issue: number
  reopen: number
  slack_message: number
  azure_boards_issue: number
  asana_task: number
  github_issue: number
}

export type ActionsSummaryResponse = KosmosApi.Response<ActionsStatistics>

export interface OverTimeStatistic {
  weekEnd: string
  weekStart: string
  actions: ActionsStatistics
}

export type ActionsOverTimeResponse = KosmosApi.Response<OverTimeStatistic[]>

export interface TestActionRequest {
  action: Action
  trigger_type: string
}

export interface JiraTestResult {
  ticketKey: string
  ticketSummary: string
  ticketUrl: string
}

export interface TicketTestResult extends JiraTestResult {
  vendor: TicketingSystem
}

export interface SlackTestResult {
  channelNames: string[]
}

export type TestActionResponse = KosmosApi.Response<
  JiraTestResult | TicketTestResult | SlackTestResult | null
> & {
  error?: ActionError
}
